/**
 * workOrderEdit.js: what a work order's Edit form opens with, and what its
 * Save writes (IDI-440).
 *
 * IDI-440 (Urgent, walk A1): Edit on a MIGRATED work order opened with the
 * schedule blank, the crew as a raw id and the type as "Standard", because
 * Edit read a different set of field names than the view:
 *   - schedule: migrated WOs carry a full ISO start (`startAt`, mapped to
 *     `scheduledDate` by the jobs API), e.g. "2024-01-06T08:00:00Z", with no
 *     separate HH:mm. The DatePicker needs YYYY-MM-DD and the TimePicker
 *     HH:mm, so both rendered blank.
 *   - type: migrated WOs carry `typeId` (legacy id), and the jobs API adds
 *     `type` (its label). Edit and the view read only `woType`.
 *   - crew: the crew is resolved by id, but an INACTIVE crew isn't in the
 *     picker's options, so the chip showed the raw id.
 * Saving that form wrote the blanks over the migrated values.
 *
 * Rule: the form opens with what's stored, read through the same aliases the
 * view uses, and Save writes only fields the user changed.
 */
import { woSchedule } from './woSchedule';

/** The type value the form should select: a settings-list id, or the label. */
export function woTypeValue(wo) {
  // IDI-517: same rule as the jobs API's woHasType: the first key holding a
  // non-empty string or a number (a legacy numeric id of 0 still counts).
  for (const k of ['woType', 'wo_type', 'typeId', 'type_id']) {
    const v = wo?.[k];
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'string' && v !== '') return v;
  }
  return '';
}

/**
 * IDI-470: what an empty type reads as. A dealer's types are their own
 * (tenant 1: Install Batts, Install Blow, Prelim, Repair, Spray Foam) and
 * none is "Standard", so the old fallback invented a type nobody had.
 */
export const NO_WO_TYPE_LABEL = 'No type';

/**
 * IDI-517: a work order needs a type (it drives reporting). Create and Edit
 * refuse to save without one; the jobs API sends the same message.
 */
export const WO_TYPE_REQUIRED_MSG = 'Choose a work-order type';

/** Label for a WO's type, for the view and the form. */
export function woTypeLabel(wo, getEnumLabel) {
  const raw = woTypeValue(wo);
  const stored = wo?.type || wo?.typeLabel || '';
  if (!raw) return stored || NO_WO_TYPE_LABEL;
  const fromList = getEnumLabel ? getEnumLabel('work_order_types', raw) : '';
  if (fromList && fromList !== raw) return fromList;
  return stored || fromList || raw;
}

/** The schedule fields the Edit form shows, in local time. */
export function scheduleForEdit(wo) {
  // IDI-471: the stop has its own date (endDate / stopAt); it used to be
  // assumed to be the start date. One reader with every view: utils/woSchedule.
  const sched = woSchedule(wo);
  return {
    scheduledDate: sched.startDate,
    startTime: sched.startTime,
    endDate: sched.stopDate,
    endTime: sched.stopTime,
  };
}

/**
 * The scalar fields Save writes: only those whose value differs from what the
 * form opened with. An untouched field is never written, so a migrated value
 * the form can't represent exactly is never replaced by its display form.
 */
export function changedScalarFields(seed, draft, keys) {
  const out = {};
  for (const k of keys) {
    const before = seed?.[k] ?? '';
    const after = draft?.[k] ?? '';
    if (String(before) !== String(after)) out[k] = after === '' ? null : after;
  }
  return out;
}

/**
 * IDI-470: the work order's number for DISPLAY, or '' when it has none.
 * Unlike displayId.formatWorkOrderNumber this never falls back to the raw id
 * (`wo-1790700557444-ba4etu`), which is not something a person should read.
 * Short numbers come with the document-numbers ticket; until then a net-new
 * WO simply shows no number.
 */
export function woDisplayNumber(wo) {
  const n = wo?.number;
  return n != null && String(n).trim() !== '' ? String(n).trim() : '';
}

/**
 * IDI-470: the row's title and the metadata beneath it. The title is the typed
 * title (the create sheet pre-fills one); the type and number go beneath.
 * With no title, the type leads (migrated WOs carry none).
 */
export function woRowHeading(wo, getEnumLabel) {
  const title = typeof wo?.title === 'string' ? wo.title.trim() : '';
  const type = woTypeLabel(wo, getEnumLabel);
  const number = woDisplayNumber(wo);
  if (title) return { title, meta: [type, number].filter(Boolean).join(' \u00b7 ') };
  return { title: type, meta: number };
}
